// File: src/pages/MyStuff.tsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/Lp2App.css";
import {
  bootloopClient,
  IncomingInterestDto,
  conditionLabel,
  shoeTypeLabel,
} from "../api/bootloopClient";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

const LS_EMAIL = "bootloop.email";
const LS_POSTCODE = "bootloop.postcode";
const LS_MY_INTERESTS = "bootloop.myInterests";

type MyListingApi = {
  listingId: number;
  ownerUserId: number;
  sizeUk: number;
  shoeType: number;
  condition: number;
  notes?: string | null;
  photoUrl?: string | null;
  status: number;
  createdUtc: string;
  lastActivityUtc: string;
};

type MyInterestLocal = {
  listingId: number;
  interestId?: number;
  createdUtc: string;
  email: string;
  postcode?: string | null;

  sizeUk: number;
  shoeType: number;
  condition: number;
  notes?: string | null;
  photoUrl?: string | null;
  distanceMiles?: number | null;
};

function normEmail(s: string) {
  return (s ?? "").trim().toLowerCase();
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, val: any) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* ignore */
  }
}

function fmtWhen(isoOrDate: string) {
  try {
    return new Date(isoOrDate).toLocaleString();
  } catch {
    return isoOrDate;
  }
}

// Optional: mask emails for privacy on shared screens
function maskEmail(email?: string | null) {
  if (!email) return "—";
  const e = email.trim();
  const at = e.indexOf("@");
  if (at <= 1) return "—";
  return `${e[0]}***${e.slice(at)}`; // p***@domain.com
}

/* ------------------------------------------------------------
   Listing status helpers (BootListings.Status)
   1 = Active
   2 = Expired
   3 = Swapped
   (4 = Removed optional)
------------------------------------------------------------ */

function listingStatusLabel(status?: number | null) {
  switch (Number(status)) {
    case 1:
      return "Active";
    case 2:
      return "Expired";
    case 3:
      return "Swapped";
    case 4:
      return "Removed";
    default:
      return "—";
  }
}

function listingStatusBadgeClass(status?: number | null) {
  switch (Number(status)) {
    case 1:
      return "lp2app__badge lp2app__badge--ok";
    case 2:
      return "lp2app__badge lp2app__badge--muted";
    case 3:
      return "lp2app__badge lp2app__badge--warn";
    case 4:
      return "lp2app__badge lp2app__badge--muted";
    default:
      return "lp2app__badge lp2app__badge--muted";
  }
}

async function apiGetMyListings(ownerEmail: string): Promise<MyListingApi[]> {
  const url = apiUrl(
    `/api/listings/my?ownerEmail=${encodeURIComponent(
      ownerEmail
    )}&includeInactive=true&daysBack=365&maxRows=200`
  );

  const res = await fetch(url, { method: "GET" });

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const text = await res.text();

  if (!res.ok) {
    if (ct.includes("application/json")) {
      try {
        const j = JSON.parse(text);
        const msg =
          j?.detail || j?.title || j?.message || `Failed (${res.status})`;
        throw new Error(msg);
      } catch {
        throw new Error(`Failed (${res.status})`);
      }
    }
    throw new Error(`Failed (${res.status}): ${text.slice(0, 200)}`);
  }

  if (!ct.includes("application/json")) {
    throw new Error(
      `Expected JSON from API but got '${ct || "unknown"}' (${res.url}). First bytes: ${text.slice(
        0,
        80
      )}`
    );
  }

  return (JSON.parse(text) as MyListingApi[]) ?? [];
}

/**
 * PUT /api/listings/{listingId}/status
 * Body: { status: 1|3, ownerEmail }
 */
async function apiSetListingStatus(
  listingId: number,
  status: number,
  ownerEmail: string
) {
  const url = apiUrl(`/api/listings/${listingId}/status`);

  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, ownerEmail }),
  });

  if (res.status === 204) return;

  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const text = await res.text();

  if (ct.includes("application/json")) {
    try {
      const j = JSON.parse(text);
      throw new Error(
        j?.detail || j?.title || j?.message || `Failed (${res.status})`
      );
    } catch {
      throw new Error(`Failed (${res.status})`);
    }
  }

  throw new Error(`Failed (${res.status}): ${text.slice(0, 200)}`);
}

const MyStuff: React.FC = () => {
  const [email, setEmail] = useState(() => localStorage.getItem(LS_EMAIL) ?? "");
  const [postcode, setPostcode] = useState(
    () => localStorage.getItem(LS_POSTCODE) ?? ""
  );

  const [incomingBusy, setIncomingBusy] = useState(false);
  const [incomingErr, setIncomingErr] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<IncomingInterestDto[]>([]);

  const [replyBusyId, setReplyBusyId] = useState<number | null>(null);
  const [replyErr, setReplyErr] = useState<string | null>(null);
  const [replyOk, setReplyOk] = useState<string | null>(null);

  const [myListingsBusy, setMyListingsBusy] = useState(false);
  const [myListingsErr, setMyListingsErr] = useState<string | null>(null);
  const [myListings, setMyListings] = useState<MyListingApi[]>([]);

  const [statusBusyListingId, setStatusBusyListingId] = useState<number | null>(
    null
  );
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [statusOk, setStatusOk] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(LS_EMAIL, email);
  }, [email]);

  useEffect(() => {
    localStorage.setItem(LS_POSTCODE, postcode);
  }, [postcode]);

  const myInterests = useMemo(() => {
    const em = normEmail(email);
    return loadJson<MyInterestLocal[]>(LS_MY_INTERESTS, []).filter(
      (x) => normEmail(x.email ?? "") === em
    );
  }, [email]);

  async function loadMyListings() {
    setMyListingsErr(null);
    setStatusErr(null);
    setStatusOk(null);

    const em = email.trim();
    if (!em) {
      setMyListingsErr("Enter your email above to load your listings.");
      setMyListings([]);
      return;
    }

    try {
      setMyListingsBusy(true);
      const rows = await apiGetMyListings(em);
      setMyListings(rows ?? []);
    } catch (e: any) {
      setMyListingsErr(e?.message || "Failed to load your listings.");
      setMyListings([]);
    } finally {
      setMyListingsBusy(false);
    }
  }

  async function loadIncoming() {
    setIncomingErr(null);
    setReplyErr(null);
    setReplyOk(null);
    setStatusErr(null);
    setStatusOk(null);

    const em = email.trim();
    if (!em) {
      setIncomingErr(
        "Enter your email above to load incoming interests for your listings."
      );
      setIncoming([]);
      return;
    }

    try {
      setIncomingBusy(true);
      const rows = await bootloopClient.getMyIncomingInterests({
        ownerEmail: em,
        daysBack: 30,
        maxRows: 200,
      });
      setIncoming(rows ?? []);
    } catch (e: any) {
      setIncomingErr(e?.message || "Failed to load incoming interests.");
      setIncoming([]);
    } finally {
      setIncomingBusy(false);
    }
  }

  async function sendReplyLink(interestId: number) {
    setReplyErr(null);
    setReplyOk(null);
    setStatusErr(null);
    setStatusOk(null);

    const em = email.trim();
    if (!em) {
      setReplyErr("Enter your email first.");
      return;
    }

    try {
      setReplyBusyId(interestId);
      await bootloopClient.createReplyLink(interestId, { ownerEmail: em });
      setReplyOk("✅ Reply link sent (check their email).");
    } catch (e: any) {
      setReplyErr(e?.message || "Failed to send reply link.");
    } finally {
      setReplyBusyId(null);
    }
  }

  async function setListingStatus(listingId: number, status: number) {
    setStatusErr(null);
    setStatusOk(null);
    setReplyErr(null);
    setReplyOk(null);

    const em = email.trim();
    if (!em) {
      setStatusErr("Enter your email first (used to verify ownership).");
      return;
    }

    try {
      setStatusBusyListingId(listingId);
      await apiSetListingStatus(listingId, status, em);
      await loadMyListings();
      setStatusOk(
        `✅ Listing #${listingId} set to ${listingStatusLabel(status)}.`
      );
    } catch (e: any) {
      setStatusErr(e?.message || "Failed to update listing status.");
    } finally {
      setStatusBusyListingId(null);
    }
  }

  function clearLocalInterests() {
    saveJson(LS_MY_INTERESTS, []);
    window.location.reload();
  }

  useEffect(() => {
    const em = (localStorage.getItem(LS_EMAIL) ?? "").trim();
    if (em) loadMyListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="lp2app">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-end",
        }}
      >
        <div>
          <h1 className="lp2app__title">My stuff</h1>
          <p className="lp2app__lead">
            Your listings, interests you’ve sent, and interests received.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Link className="lp2app__btnSecondary" to="/find-swap-v2">
            Find a swap
          </Link>
          <Link className="lp2app__btnSecondary" to="/list-boot">
            List boots
          </Link>
        </div>
      </div>

      <div className="lp2app__card" style={{ marginTop: 18 }}>
        <div className="lp2app__row2">
          <label className="lp2app__label">
            Your email (used to load your listings + incoming interests)
            <input
              className="lp2app__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
            />
          </label>

          <label className="lp2app__label">
            Postcode (optional)
            <input
              className="lp2app__input"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. CO1 1AA"
            />
          </label>
        </div>

        <div
          className="lp2app__actions"
          style={{ justifyContent: "flex-start", marginTop: 12, gap: 10 }}
        >
          <button
            className="lp2app__btnPrimary"
            type="button"
            onClick={loadMyListings}
            disabled={myListingsBusy}
          >
            {myListingsBusy ? "Loading…" : "Load my listings"}
          </button>

          <button
            className="lp2app__btnPrimary"
            type="button"
            onClick={loadIncoming}
            disabled={incomingBusy}
          >
            {incomingBusy ? "Loading…" : "Load incoming interests"}
          </button>

          <button
            className="lp2app__btnSecondary"
            type="button"
            onClick={clearLocalInterests}
          >
            Clear local interests
          </button>
        </div>

        {myListingsErr && (
          <div className="lp2app__alert" style={{ marginTop: 12 }}>
            {myListingsErr}
          </div>
        )}
        {incomingErr && (
          <div className="lp2app__alert" style={{ marginTop: 12 }}>
            {incomingErr}
          </div>
        )}
        {replyErr && (
          <div className="lp2app__alert" style={{ marginTop: 12 }}>
            {replyErr}
          </div>
        )}
        {replyOk && (
          <div className="lp2app__success" style={{ marginTop: 12 }}>
            {replyOk}
          </div>
        )}
        {statusErr && (
          <div className="lp2app__alert" style={{ marginTop: 12 }}>
            {statusErr}
          </div>
        )}
        {statusOk && (
          <div className="lp2app__success" style={{ marginTop: 12 }}>
            {statusOk}
          </div>
        )}

        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
          Tip: <b>Expired</b> listings are hidden from search automatically. Use{" "}
          <b>Re-activate</b> to show them again.
        </div>
      </div>

{/* My Listings (API) */}
<div className="lp2app__card" style={{ marginTop: 18 }}>
  <h3 style={{ marginTop: 0 }}>My listings</h3>

  <div className="lp2app__tableWrap">
    <table className="lp2app__table">
      <thead>
        <tr>
          <th>Listing</th>
          <th>Size</th>
          <th>Type</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        {myListings.map((x) => {
          const st = Number(x.status);
          const canMarkSwapped = st === 1;
          const canReactivate = st === 2 || st === 3;

          return (
            <tr key={x.listingId}>
              {/* Listing (click-through) */}
              <td>
                <Link
                  className="lp2app__rowLink"
                  to={`/boots/${x.listingId}`}
                >
                  #{x.listingId}
                </Link>
              </td>

              <td>{x.sizeUk}</td>

              <td>{shoeTypeLabel(Number(x.shoeType))}</td>

              <td>
                <span className={listingStatusBadgeClass(st)}>
                  {listingStatusLabel(st)}
                </span>
              </td>

              <td className="lp2app__actionsCell">
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {canMarkSwapped && (
                    <button
                      className="lp2app__btnSecondary lp2app__btnNoWrap"
                      type="button"
                      disabled={statusBusyListingId === x.listingId}
                      onClick={() => setListingStatus(x.listingId, 3)}
                    >
                      {statusBusyListingId === x.listingId
                        ? "Saving…"
                        : "Mark swapped"}
                    </button>
                  )}

                  {canReactivate && (
                    <button
                      className="lp2app__btnSecondary lp2app__btnNoWrap"
                      type="button"
                      disabled={statusBusyListingId === x.listingId}
                      onClick={() => setListingStatus(x.listingId, 1)}
                    >
                      {statusBusyListingId === x.listingId
                        ? "Saving…"
                        : "Re-activate"}
                    </button>
                  )}

                  {!canMarkSwapped && !canReactivate && (
                    <span style={{ fontSize: 12, opacity: 0.7 }}>—</span>
                  )}
                </div>
              </td>
            </tr>
          );
        })}

        {!myListingsBusy && myListings.length === 0 && (
          <tr>
            <td colSpan={5} style={{ opacity: 0.8, padding: 12 }}>
              No listings returned. Click “Load my listings”.
            </td>
          </tr>
        )}

        {myListingsBusy && (
          <tr>
            <td colSpan={5} style={{ opacity: 0.8, padding: 12 }}>
              Loading…
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* Interests Sent (local) */}
      <div className="lp2app__card" style={{ marginTop: 18 }}>
        <h3 style={{ marginTop: 0 }}>Interests I’ve sent (local)</h3>
        <div className="lp2app__tableWrap">
          <table className="lp2app__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Listing</th>
                <th>Size</th>
                <th>Type</th>
                <th>Condition</th>
                <th>Distance</th>
              </tr>
            </thead>
            <tbody>
              {myInterests.map((x) => (
                <tr key={`${x.listingId}-${x.createdUtc}`}>
                  <td>{fmtWhen(x.createdUtc)}</td>
                  <td>#{x.listingId}</td>
                  <td>{x.sizeUk}</td>
                  <td>{shoeTypeLabel(Number(x.shoeType))}</td>
                  <td>{conditionLabel(Number(x.condition))}</td>
                  <td>
                    {x.distanceMiles != null
                      ? `${x.distanceMiles.toFixed(1)} mi`
                      : "—"}
                  </td>
                </tr>
              ))}
              {myInterests.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ opacity: 0.8, padding: 12 }}>
                    No local interests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Incoming interests */}
      <div className="lp2app__card" style={{ marginTop: 18 }}>
        <h3 style={{ marginTop: 0 }}>Interests received (from API)</h3>

        <div className="lp2app__tableWrap">
          <table className="lp2app__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Listing</th>
                <th>Interested</th>
                <th>Postcode</th>
                <th>Boot</th>
                <th>Reply</th>
              </tr>
            </thead>
            <tbody>
              {incoming.map((x) => (
                <tr key={x.interestId}>
                  <td>{fmtWhen(x.createdUtc)}</td>
                  <td>#{x.listingId}</td>
                  <td>{maskEmail(x.interestedEmail)}</td>
                  <td>{x.interestedPostcode ?? "—"}</td>
                  <td className="wrap">
                    Size {x.sizeUk} · {shoeTypeLabel(Number(x.shoeType))} ·{" "}
                    {conditionLabel(Number(x.condition))}
                  </td>

                  <td className="lp2app__actionsCell">
                    <button
                      className="lp2app__btnPrimary lp2app__btnNoWrap"
                      type="button"
                      disabled={replyBusyId === x.interestId}
                      onClick={() => sendReplyLink(x.interestId)}
                    >
                      {replyBusyId === x.interestId
                        ? "Sending…"
                        : "Send reply link"}
                    </button>

                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                      Sends them a secure email link to reply.
                    </div>
                  </td>
                </tr>
              ))}

              {incoming.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ opacity: 0.8, padding: 12 }}>
                    No incoming interests loaded. Click “Load incoming interests”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyStuff;